import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Shield, Award, MapPin, Building } from "lucide-react";
import { BRAND, IMG } from "../lib/content";

const trust = [
  { icon: Shield, label: "Fully insured" },
  { icon: Award, label: "50+ yrs combined" },
  { icon: Building, label: "Residential & commercial" },
  { icon: MapPin, label: "Sydney-wide" },
];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0A0A]">
      {/* Background — real grand foyer with chandelier and checkered marble */}
      <div className="absolute inset-0">
        <img
          src={IMG.grandFoyer}
          alt="Grand marble foyer with checkered floor and chandelier — Reflex Stone Care project"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0.55)_100%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-16 min-h-[100svh] flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 text-[#E8DAB8]"
        >
          <span className="block w-10 h-px bg-[#C9A86A]" />
          <span className="text-[11px] uppercase tracking-[0.3em]">
            Sydney's premium stone restoration
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="font-display-bold mt-7 text-[44px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[88px] tracking-tighter text-white max-w-5xl"
        >
          Bring your stone surfaces
          <br />
          <span className="font-serif-italic font-light text-[#E8DAB8]">back to life.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.32 }}
          className="mt-8 text-white/80 max-w-2xl text-base md:text-lg leading-relaxed"
        >
          Premium marble, terrazzo and natural stone restoration across Sydney —
          delivered with the precision of a European stone trade and the
          finish standards of a five-star hotel.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.48 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-btn"
            className="group inline-flex items-center gap-3 px-8 md:px-9 py-4 bg-[#C9A86A] text-black uppercase tracking-[0.22em] text-[11px] hover:bg-[#B8965A] transition-colors"
          >
            Get free quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={BRAND.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center gap-3 px-8 md:px-9 py-4 border border-white/30 text-white uppercase tracking-[0.22em] text-[11px] hover:bg-white/10 transition-colors"
          >
            <Phone className="w-4 h-4" /> Call {BRAND.phone}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-auto pt-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-6 border-t border-white/15 pt-8">
            {trust.map((t) => (
              <div key={t.label} className="flex items-center gap-3 text-white/85">
                <t.icon className="w-4 h-4 text-[#C9A86A]" strokeWidth={1.4} />
                <span className="text-[11px] md:text-xs uppercase tracking-[0.2em]">
                  {t.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.6 }}
        className="absolute bottom-6 right-6 hidden md:flex items-center gap-3 text-white/55 text-[10px] uppercase tracking-[0.3em]"
      >
        Scroll
        <span className="block w-12 h-px bg-white/40" />
      </motion.div>
    </section>
  );
}
